import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrescriptionsTab = ({ onNewPrescription }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const prescriptions = [
    {
      id: "RX-2025-001",
      medication: "Lisinopril",
      strength: "10mg",
      form: "Tablet",
      quantity: "30",
      refills: "5",
      refillsUsed: "2",
      dosage: "Once daily",
      instructions: "Take with or without food. Monitor blood pressure regularly.",
      prescribedBy: "Dr. Sarah Johnson",
      prescribedDate: "2025-09-20",
      pharmacy: "HealthMart Pharmacy",
      pharmacyPhone: "(555) 123-4567",
      status: "Active",
      lastFilled: "2025-09-20",
      nextRefillDate: "2025-10-20",
      indication: "Hypertension",
      ndc: "0093-1058-01",
      daysSupply: "30",
      documents: ["Prescription_Lisinopril_Sept2025.pdf", "FHIR_Prescription_001.json"]
    },
    {
      id: "RX-2025-002",
      medication: "Metformin",
      strength: "500mg",
      form: "Tablet",
      quantity: "60",
      refills: "3",
      refillsUsed: "1",
      dosage: "Twice daily with meals",
      instructions: "Take with breakfast and dinner. Monitor blood glucose levels.",
      prescribedBy: "Dr. Michael Chen",
      prescribedDate: "2025-09-15",
      pharmacy: "CVS Pharmacy",
      pharmacyPhone: "(555) 987-6543",
      status: "Active",
      lastFilled: "2025-09-15",
      nextRefillDate: "2025-10-15",
      indication: "Type 2 Diabetes",
      ndc: "0093-1045-01",
      daysSupply: "30",
      documents: ["Prescription_Metformin_Sept2025.pdf", "FHIR_Prescription_002.json"]
    },
    {
      id: "RX-2025-003",
      medication: "Albuterol",
      strength: "90mcg",
      form: "Inhaler",
      quantity: "1",
      refills: "2",
      refillsUsed: "0",
      dosage: "2 puffs as needed",
      instructions: "Use for shortness of breath or wheezing. Rinse mouth after use.",
      prescribedBy: "Dr. Emily Rodriguez",
      prescribedDate: "2025-09-10",
      pharmacy: "Walgreens",
      pharmacyPhone: "(555) 456-7890",
      status: "Active",
      lastFilled: "2025-09-10",
      nextRefillDate: "2025-12-10",
      indication: "Asthma",
      ndc: "0173-0682-20",
      daysSupply: "90",
      documents: ["Prescription_Albuterol_Sept2025.pdf", "FHIR_Prescription_003.json"]
    },
    {
      id: "RX-2025-004",
      medication: "Amoxicillin",
      strength: "500mg",
      form: "Capsule",
      quantity: "21",
      refills: "0",
      refillsUsed: "0",
      dosage: "Three times daily",
      instructions: "Take every 8 hours with food. Complete entire course even if feeling better.",
      prescribedBy: "Dr. Sarah Johnson",
      prescribedDate: "2025-08-25",
      pharmacy: "HealthMart Pharmacy",
      pharmacyPhone: "(555) 123-4567",
      status: "Completed",
      lastFilled: "2025-08-25",
      nextRefillDate: "N/A",
      indication: "Bacterial Infection",
      ndc: "0093-4155-01",
      daysSupply: "7",
      documents: ["Prescription_Amoxicillin_Aug2025.pdf", "FHIR_Prescription_004.json"]
    }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-success text-success-foreground';
      case 'completed': return 'bg-muted text-muted-foreground';
      case 'cancelled': return 'bg-error text-error-foreground';
      case 'expired': return 'bg-warning text-warning-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getRefillStatus = (refills, refillsUsed) => {
    const remaining = parseInt(refills) - parseInt(refillsUsed);
    if (remaining === 0) return { text: 'No refills', color: 'text-error' };
    if (remaining === 1) return { text: '1 refill left', color: 'text-warning' };
    return { text: `${remaining} refills left`, color: 'text-success' };
  };

  const filteredPrescriptions = prescriptions?.filter(prescription => {
    const matchesSearch = prescription?.medication?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         prescription?.prescribedBy?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         prescription?.indication?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || prescription?.status?.toLowerCase() === filterStatus?.toLowerCase();
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-clinical">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search prescriptions by medication, provider, or indication..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-4 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
            
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onNewPrescription}>
              New Prescription
            </Button>
          </div>
        </div>
      </div>
      {/* Prescriptions List */}
      <div className="space-y-4">
        {filteredPrescriptions?.map((prescription) => (
          <div key={prescription?.id} className="bg-card border border-border rounded-lg shadow-clinical">
            {/* Prescription Header */}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Pill" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">
                        {prescription?.medication} {prescription?.strength}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(prescription?.status)}`}>
                        {prescription?.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        {prescription?.prescribedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {new Date(prescription.prescribedDate)?.toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Building" size={14} />
                        {prescription?.pharmacy}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="RefreshCw">
                    Request Refill
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download">
                    Download
                  </Button>
                </div>
              </div>
            </div>

            {/* Prescription Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Medication Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Form:</span>
                        <span className="text-foreground">{prescription?.form}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quantity:</span>
                        <span className="text-foreground">{prescription?.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Days Supply:</span>
                        <span className="text-foreground">{prescription?.daysSupply} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">NDC:</span>
                        <span className="text-foreground">{prescription?.ndc}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Dosage & Instructions</h4>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-sm font-medium text-foreground mb-2">{prescription?.dosage}</p>
                      <p className="text-sm text-muted-foreground">{prescription?.instructions}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Indication</h4>
                    <div className="flex items-center gap-2">
                      <Icon name="Target" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{prescription?.indication}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Refill Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Refills:</span>
                        <span className="text-foreground">{prescription?.refills}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Refills Used:</span>
                        <span className="text-foreground">{prescription?.refillsUsed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Remaining:</span>
                        <span className={getRefillStatus(prescription?.refills, prescription?.refillsUsed)?.color}>
                          {getRefillStatus(prescription?.refills, prescription?.refillsUsed)?.text}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Filled:</span>
                        <span className="text-foreground">{new Date(prescription.lastFilled)?.toLocaleDateString()}</span>
                      </div>
                      {prescription?.nextRefillDate !== 'N/A' && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Next Refill:</span>
                          <span className="text-foreground">{new Date(prescription.nextRefillDate)?.toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Pharmacy Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Icon name="Building" size={16} className="text-muted-foreground" />
                        <span className="text-foreground">{prescription?.pharmacy}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <span className="text-foreground">{prescription?.pharmacyPhone}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-3">Documents</h4>
                    <div className="space-y-2">
                      {prescription?.documents?.map((document, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                          <div className="flex items-center gap-2">
                            <Icon 
                              name={document?.includes('.pdf') ? 'FileText' : 'Code'} 
                              size={16} 
                              className="text-muted-foreground" 
                            />
                            <span className="text-sm text-foreground">{document}</span>
                          </div>
                          <Button variant="ghost" size="sm" iconName="Download">
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredPrescriptions?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center shadow-clinical">
          <Icon name="Pill" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No prescriptions found</h3>
          <p className="text-muted-foreground mb-6">No prescriptions match your current search criteria.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={onNewPrescription}>
            Create New Prescription
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrescriptionsTab;